import { useId, useState } from "react";

import {
  errorMessage,
  useAdminUsers,
  useApproveUser,
  useAudit,
  useDisableUser,
  useSetPaused,
  useStorageHealth,
} from "../api/queries";
import type { AdminUser, BucketHealth, StorageHealth } from "../api/types";
import { useSession } from "../auth/SessionProvider";
import { useStepUp } from "../auth/useStepUp";
import { isStorageProblem, StorageProblem } from "../components/StorageProblem";
import { PageHeader } from "../components/layout/PageHeader";
import { Dialog } from "../components/ui/Dialog";
import { Field } from "../components/ui/Field";
import {
  EmptyBlock,
  ErrorBlock,
  LoadingBlock,
} from "../components/ui/StateBlock";
import {
  formatBytes,
  formatCount,
  formatDateTime,
  formatSeconds,
  pluralize,
  UNKNOWN,
} from "../lib/format";

/** One bucket's line: a bucket that could not be read says so and keeps its name. */
function BucketRow({
  label,
  bucket,
  reason,
}: {
  label: string;
  bucket: BucketHealth | null;
  reason: string | null;
}): JSX.Element {
  if (!bucket) {
    return (
      <tr>
        <td>{label}</td>
        <td colSpan={4} className="admin-table__muted">
          {reason ?? "Not configured on this console."}
        </td>
      </tr>
    );
  }
  return (
    <tr>
      <td>
        <span className="admin-table__name">{label}</span>
        <span className="admin-table__sub mono">{bucket.name}</span>
      </td>
      <td className="mono">{bucket.region}</td>
      <td>{formatCount(bucket.objects)}</td>
      <td>{formatBytes(bucket.bytes)}</td>
      <td className={bucket.error ? "" : "admin-table__muted"}>
        {bucket.error ?? "read without trouble"}
      </td>
    </tr>
  );
}

/** Storage health, and the honest one. */
function StorageSection({ health }: { health: StorageHealth }): JSX.Element {
  const replication = health.replication;
  return (
    <section className="section">
      <div className="section__head">
        <h2 className="section__title">Where the files are</h2>
        <span className="section__meta">
          {formatBytes(health.stored_bytes)} held · this console is set up for{" "}
          {health.max_stored_tb} TB
        </span>
      </div>

      <table className="admin-table">
        <thead>
          <tr>
            <th scope="col">Which</th>
            <th scope="col">Region</th>
            <th scope="col">Files</th>
            <th scope="col">Size</th>
            <th scope="col">State</th>
          </tr>
        </thead>
        <tbody>
          <BucketRow
            label="Holding area"
            bucket={health.buckets.quarantine}
            reason={null}
          />
          <BucketRow label="The vault" bucket={health.buckets.vault} reason={null} />
          <BucketRow
            label="The second copy"
            bucket={health.buckets.replica}
            reason={replication?.reason ?? "No second copy is configured on this console."}
          />
        </tbody>
      </table>

      <div className="section__head">
        <h3 className="section__title">The second copy</h3>
      </div>
      {!replication || !replication.measured ? (
        <p className="state-block">
          {replication?.reason ??
            "No second copy is configured on this console."}{" "}
          Nothing is refused for it: a file is usable with one copy, and the
          console does not claim a copy it has not looked for.
        </p>
      ) : (
        <p
          className="state-block"
          data-tone={replication.alert ? "warning" : "ok"}
        >
          The oldest file with no confirmed copy is{" "}
          {formatSeconds(replication.lag_seconds)} old, and{" "}
          {formatCount(replication.unreplicated_versions)} are waiting. The
          copying job runs every {replication.interval_minutes} minutes and this
          screen turns amber past {formatSeconds(replication.alert_seconds)}.
          {replication.alert
            ? " It is past that now — worth a look, and not an outage: nobody is refused anything for it."
            : ""}
        </p>
      )}

      <div className="section__head">
        <h3 className="section__title">Files nobody can explain</h3>
      </div>
      {health.orphans.length === 0 ? (
        <EmptyBlock label="None. Every file in the vault has a record behind it." />
      ) : (
        <>
          <p className="section__lead">
            These have no record behind them, so the console moved them aside
            and listed them. It never deletes what it cannot explain.
          </p>
          <table className="admin-table">
            <thead>
              <tr>
                <th scope="col">Where it was moved</th>
                <th scope="col">Size</th>
                <th scope="col">When</th>
              </tr>
            </thead>
            <tbody>
              {health.orphans.map((orphan) => (
                <tr key={orphan.key}>
                  <td className="mono admin-table__wrap">{orphan.key}</td>
                  <td>{formatBytes(orphan.bytes)}</td>
                  <td className="admin-table__muted">
                    {formatDateTime(orphan.moved_at)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </>
      )}

      {health.keys_over_velocity.length > 0 ? (
        <>
          <div className="section__head">
            <h3 className="section__title">Keys asking for a lot of links</h3>
          </div>
          <p className="section__lead">
            Each of these tripped the ceiling on one credential in the last day.
            It is a guard on a key's blast radius, not a limit on a person's
            reading — but a key doing this is worth a question.
          </p>
          <ul className="ledger">
            {health.keys_over_velocity.map((key) => (
              <li
                className="ledger__row"
                key={`${key.key_prefix}-${key.last_at}`}
              >
                <span className="ledger__permissions mono">
                  dk_{key.key_prefix}…
                </span>
                <span className="ledger__evidence">
                  {key.owner} · {pluralize(key.refusals, "refusal")}
                </span>
                <span className="ledger__meta">
                  {formatDateTime(key.last_at)}
                </span>
              </li>
            ))}
          </ul>
        </>
      ) : null}

      <p className="form__note">
        {/*
          The server's own sentence, measured while the payload was built: it
          names the engine and its signatures, or says the scanner did not
          answer — the loudest state on purpose (ADR-0018). The fallbacks are
          only for a reason the server did not send.
        */}
        {health.scan.reason ??
          (health.scan.performed
            ? "Files are scanned on this console."
            : "Not scanned for malware — no scanner is configured.")}{" "}
        The reading pass has {pluralize(health.verify.queued, "file")} waiting
        and {health.verify.running} in hand, {health.verify.concurrency} at a
        time. The console last completed a pass{" "}
        {formatDateTime(health.reconcile.last_pass_at)}.
      </p>
    </section>
  );
}

/** Turn an account off, with the reason that goes in the audit. */
function DisableDialog({
  user,
  onClose,
}: {
  user: AdminUser;
  onClose: () => void;
}): JSX.Element {
  const reasonId = useId();
  const [reason, setReason] = useState("");
  const disable = useDisableUser();

  return (
    <Dialog
      title={`Turn off ${user.username}`}
      onClose={onClose}
      footer={
        <>
          <button type="button" className="btn btn--ghost" onClick={onClose}>
            Cancel
          </button>
          <button
            type="button"
            className="btn btn--danger"
            disabled={disable.isPending || reason.trim() === ""}
            onClick={() =>
              disable.mutate(
                { username: user.username, reason: reason.trim() },
                { onSuccess: onClose },
              )
            }
          >
            {disable.isPending ? "Turning off…" : "Turn it off"}
          </button>
        </>
      }
    >
      <p className="dialog__text">
        They keep everything: every dataset, consent and trace stays exactly as
        it is. What stops is them — and every key they hold, at its next
        request. Links already issued keep working until they expire.
      </p>
      <Field
        label="Why?"
        htmlFor={reasonId}
        hint="It goes in the audit log, and they read it on their own screen."
        error={disable.isError ? errorMessage(disable.error) : undefined}
      >
        <input
          id={reasonId}
          className="input"
          value={reason}
          onChange={(event) => setReason(event.target.value)}
        />
      </Field>
    </Dialog>
  );
}

/**
 * The admin screen: people, the audit, the storage, and the pause switch.
 *
 * Returns:
 *     The admin page.
 */
export function AdminPage(): JSX.Element {
  const { me } = useSession();
  const users = useAdminUsers();
  const audit = useAudit();
  const storage = useStorageHealth();
  const approve = useApproveUser();
  const setPaused = useSetPaused();
  const stepUp = useStepUp();
  const pauseReasonId = useId();
  const [pauseReason, setPauseReason] = useState("");
  const [disabling, setDisabling] = useState<AdminUser | null>(null);

  const header = <PageHeader eyebrow="Admin" title="People, records, storage" />;

  if (!me.is_admin) {
    return (
      <>
        {header}
        <p className="state-block">
          This screen belongs to the people who approve accounts and watch the
          storage. Yours is not one of them, which is a setting somebody else
          holds.
        </p>
      </>
    );
  }

  const approveUser = (username: string): void => {
    approve.mutate(username, {
      onError: (error) => {
        stepUp.capture(error, () => approveUser(username));
      },
    });
  };

  return (
    <>
      {header}

      <section className="section">
        <div className="section__head">
          <h2 className="section__title">People</h2>
          <span className="section__meta">
            signing in is not access — somebody has to approve each of them
          </span>
        </div>

        {users.isPending ? (
          <LoadingBlock label="Reading the accounts…" />
        ) : users.isError ? (
          <ErrorBlock error={users.error} onRetry={() => void users.refetch()} />
        ) : users.data.items.length === 0 ? (
          <EmptyBlock label="Nobody has signed in yet." />
        ) : (
          <table className="admin-table">
            <thead>
              <tr>
                <th scope="col">Who</th>
                <th scope="col">State</th>
                <th scope="col">Their code</th>
                <th scope="col">Keys</th>
                <th scope="col">First seen</th>
                <th scope="col" />
              </tr>
            </thead>
            <tbody>
              {users.data.items.map((user) => (
                <tr key={user.username}>
                  <td>
                    <span className="admin-table__name">{user.username}</span>
                    <span className="admin-table__sub">{user.email}</span>
                  </td>
                  <td>
                    <span className="badge" data-state={user.state}>
                      {user.state === "active"
                        ? "approved"
                        : user.state === "pending"
                          ? "waiting"
                          : "turned off"}
                    </span>
                    {user.disabled_reason ? (
                      <span className="admin-table__sub">
                        {user.disabled_reason}
                      </span>
                    ) : null}
                  </td>
                  <td className="admin-table__muted">
                    {user.second_factor_enrolled ? "set up" : "not set up"}
                  </td>
                  <td className="admin-table__muted">
                    {formatCount(user.console_keys)}
                  </td>
                  <td className="admin-table__muted">
                    {user.first_seen_at
                      ? formatDateTime(user.first_seen_at)
                      : UNKNOWN}
                  </td>
                  <td className="admin-table__actions">
                    {user.state === "pending" ? (
                      <button
                        type="button"
                        className="btn btn--xs"
                        disabled={approve.isPending}
                        onClick={() => approveUser(user.username)}
                      >
                        Approve
                      </button>
                    ) : null}
                    {user.state !== "disabled" ? (
                      <button
                        type="button"
                        className="btn btn--xs btn--danger"
                        onClick={() => setDisabling(user)}
                      >
                        Turn off
                      </button>
                    ) : null}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}

        {approve.isError && !stepUp.dialog ? (
          <p className="form__error" role="alert">
            {errorMessage(approve.error)}
          </p>
        ) : null}
        <p className="form__note">
          Approving somebody hands them the whole catalogue, so the console asks
          for your code first.
        </p>
      </section>

      {storage.isPending ? (
        <LoadingBlock label="Reading the storage…" />
      ) : isStorageProblem(storage.error) ? (
        <StorageProblem
          error={storage.error}
          onRetry={() => void storage.refetch()}
        />
      ) : storage.isError ? (
        <ErrorBlock error={storage.error} onRetry={() => void storage.refetch()} />
      ) : (
        <StorageSection health={storage.data} />
      )}

      <section className="section">
        <div className="section__head">
          <h2 className="section__title">The pause switch</h2>
        </div>
        <div className="admin__switch">
          <p className="admin__switch-text">
            Pausing refuses new datasets, new files and new links, console-wide.
            It never deletes anything, never revokes a key and never touches a
            record — and a file already going up keeps going.
          </p>
          <Field
            label="Why?"
            htmlFor={pauseReasonId}
            hint="Everybody sees this sentence in the refusal they get."
            error={setPaused.isError ? errorMessage(setPaused.error) : undefined}
          >
            <input
              id={pauseReasonId}
              className="input"
              value={pauseReason}
              onChange={(event) => setPauseReason(event.target.value)}
            />
          </Field>
          <button
            type="button"
            className={me.console_paused ? "btn btn--primary" : "btn btn--warning"}
            disabled={setPaused.isPending || (!me.console_paused && pauseReason.trim() === "")}
            onClick={() =>
              setPaused.mutate({
                paused: !me.console_paused,
                reason: pauseReason.trim(),
              })
            }
          >
            {me.console_paused ? "Start it again" : "Pause the console"}
          </button>
        </div>
      </section>

      <section className="section">
        <div className="section__head">
          <h2 className="section__title">The audit</h2>
          <span className="section__meta">
            every consent change, every bar, every key, and every refused read
          </span>
        </div>
        {audit.isPending ? (
          <LoadingBlock label="Reading the audit…" />
        ) : audit.isError ? (
          <ErrorBlock error={audit.error} onRetry={() => void audit.refetch()} />
        ) : audit.data.items.length === 0 ? (
          <EmptyBlock label="Nothing has been recorded yet." />
        ) : (
          <table className="admin-table">
            <thead>
              <tr>
                <th scope="col">When</th>
                <th scope="col">Who</th>
                <th scope="col">What</th>
                <th scope="col">About</th>
              </tr>
            </thead>
            <tbody>
              {audit.data.items.map((event) => (
                <tr key={event.id}>
                  <td className="admin-table__muted">
                    {formatDateTime(event.at)}
                  </td>
                  <td>{event.actor ?? "console"}</td>
                  <td className="mono">{event.action}</td>
                  <td className="admin-table__muted admin-table__wrap">
                    {event.subject ?? ""}
                    {event.detail ? (
                      <span className="admin-table__sub mono">
                        {JSON.stringify(event.detail)}
                      </span>
                    ) : null}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </section>

      {disabling ? (
        <DisableDialog user={disabling} onClose={() => setDisabling(null)} />
      ) : null}

      {stepUp.dialog}
    </>
  );
}
